import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Users, Briefcase, Target, CheckCircle2, Sparkles } from "lucide-react";
import { Section, Overline, SectionHeading } from "@/components/site/Section";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3ODc3NjYwNXww&ixlib=rb-4.1.0&q=85";

const stats = [
  { v: "14d", l: "Avg. time-to-fill" },
  { v: "92%", l: "Offer acceptance" },
  { v: "7+", l: "Industries served" },
  { v: "100%", l: "Dedicated recruiters" },
];

const values = [
  { icon: Target, title: "Faster Hiring Delivery", body: "Streamlined recruiting processes that reduce time-to-fill and secure qualified talent fast." },
  { icon: Users, title: "Specialized Talent Access", body: "Technical, professional, engineering, healthcare, legal, and leadership networks." },
  { icon: Briefcase, title: "Flexible Workforce Models", body: "Direct placement, RPO, or HR services — scaled to fit your business stage." },
  { icon: Sparkles, title: "Relationship-Driven", body: "Long-term partnerships built on transparency, communication, and quality outcomes." },
];

const services = [
  {
    tag: "01 — Direct Placement",
    title: "Permanent hires across critical functions",
    body: "We help organizations identify and hire highly qualified professionals for permanent positions across technical, professional, and leadership functions.",
    bullets: ["Industry-specialized recruiting", "Access to passive candidates", "Quality-focused screening"],
  },
  {
    tag: "02 — RPO Solutions",
    title: "Scalable recruiting that grows with you",
    body: "Recruitment Process Outsourcing built to optimize hiring operations, reduce costs, and improve hiring outcomes — flex up or down as needed.",
    bullets: ["Dedicated recruiters", "Talent pipeline development", "Recruitment analytics"],
  },
  {
    tag: "03 — HR Services",
    title: "People operations that scale",
    body: "Modern HR solutions that improve employee operations, compliance, and workforce efficiency — from onboarding to performance management.",
    bullets: ["HR operations support", "Onboarding & compliance", "Workforce planning"],
  },
];

const industries = [
  { name: "Technology & SaaS", count: "12+ roles" },
  { name: "Healthcare", count: "10+ roles" },
  { name: "Aerospace & Aviation", count: "10+ roles" },
  { name: "Engineering & Manufacturing", count: "10+ roles" },
  { name: "Finance & Accounting", count: "10+ roles" },
  { name: "Legal", count: "10+ roles" },
  { name: "Human Resources", count: "9+ roles" },
];

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="container-x pt-16 md:pt-24 pb-12 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-7 fade-up">
          <Overline className="mb-5">Workforce solutions · United States</Overline>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.02] tracking-tight text-foreground">
            Building <span className="italic text-[hsl(var(--primary))]">high-performing</span> teams for the modern workforce
          </h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-muted-foreground leading-relaxed">
            X-HRverse delivers strategic recruitment, direct placement, and HR solutions that
            help businesses hire smarter, scale faster, and retain top talent.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/contact"
              data-testid="hero-cta-consultation"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[hsl(var(--primary))] text-white font-medium hover:bg-[#1A3A31] transition-colors"
            >
              Schedule a Consultation <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md border border-foreground text-foreground font-medium hover:bg-foreground hover:text-[hsl(var(--background))] transition-colors"
            >
              Request Recruiting Support <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 fade-up">
          <div className="relative aspect-[4/5] overflow-hidden rounded-xl border border-border">
            <img src={HERO_IMG} alt="Diverse team of professionals" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent text-white">
              <p className="overline text-white/80">In partnership with</p>
              <p className="font-display text-xl mt-1">Forward-thinking US businesses</p>
            </div>
          </div>
        </div>
      </div>
      <StatsBar />
    </section>
  );
}

function StatsBar() {
  return (
    <div className="border-t border-border bg-[hsl(var(--secondary))]/40">
      <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
        {stats.map((s) => (
          <div key={s.l} className="py-6 md:py-8 px-2 md:px-6 first:pl-0">
            <div className="font-display text-3xl md:text-4xl text-[hsl(var(--primary))]">{s.v}</div>
            <div className="text-xs md:text-sm text-muted-foreground mt-1">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WhyChooseUs() {
  return (
    <Section>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Why X-HRverse"
            title="A staffing partner built for modern hiring teams."
            intro="We act as strategic workforce advisors — not transactional recruiters."
          />
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border rounded-xl overflow-hidden">
          {values.map((v) => (
            <div key={v.title} className="bg-[hsl(var(--background))] p-7 md:p-8 hover:bg-[hsl(var(--secondary))]/40 transition-colors">
              <v.icon className="h-6 w-6 text-[hsl(var(--accent))]" />
              <h3 className="mt-5 font-display text-xl">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function ServicesPreview() {
  return (
    <Section className="bg-[hsl(var(--secondary))]/40 border-y border-border">
      <SectionHeading eyebrow="Our Services" title="Three ways we help you hire and scale." />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {services.map((s) => (
          <article
            key={s.tag}
            data-testid={`home-service-${s.tag.split(" ")[0]}`}
            className="bg-[hsl(var(--background))] border border-border rounded-xl p-7 md:p-8 hover:-translate-y-1 hover:shadow-sm transition-all"
          >
            <p className="overline">{s.tag}</p>
            <h3 className="mt-4 font-display text-2xl md:text-[26px] leading-tight">{s.title}</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
            <ul className="mt-5 space-y-2">
              {s.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-[hsl(var(--primary))]" /> {b}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
      <div className="mt-10">
        <Link
          to="/services"
          data-testid="home-services-cta"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]"
        >
          Explore all services <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </Section>
  );
}

function IndustriesPreview() {
  return (
    <Section>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <SectionHeading eyebrow="Industries we serve" title="Specialized recruiting across seven verticals." />
        <Link
          to="/industries"
          data-testid="home-industries-cta"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]"
        >
          See all industries <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden">
        {industries.map((i, idx) => (
          <div
            key={i.name}
            data-testid={`industry-card-${idx}`}
            className="group bg-[hsl(var(--background))] p-7 flex items-center justify-between hover:bg-[hsl(var(--primary))] hover:text-white transition-colors cursor-default"
          >
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground group-hover:text-white/70">{String(idx + 1).padStart(2, "0")}</p>
              <h3 className="mt-1 font-display text-xl">{i.name}</h3>
            </div>
            <div className="text-xs text-muted-foreground group-hover:text-white/80">{i.count}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function FinalCta() {
  return (
    <section className="container-x pb-24 md:pb-32">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#152521] via-[#0E1311] to-[#1F1410] text-white p-10 md:p-16 border border-border">
        <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-[hsl(var(--accent))]/25 blur-3xl" />
        <div className="absolute -left-32 -bottom-32 w-96 h-96 rounded-full bg-[hsl(var(--primary))]/20 blur-3xl" />
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Overline className="!text-[#6BDFB8] mb-4">Let's build your workforce</Overline>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.1] tracking-tight">
              Hiring should be a competitive advantage — not a bottleneck.
            </h2>
            <p className="mt-4 text-white/70 max-w-xl">
              Tell us about your hiring goals. We'll respond within one business day with a tailored plan.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              data-testid="home-final-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[hsl(var(--accent))] text-white font-medium hover:bg-[#c84d2e] transition-colors"
            >
              Get in touch <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <WhyChooseUs />
      <ServicesPreview />
      <IndustriesPreview />
      <FinalCta />
    </div>
  );
}
